import { useVirtualizer } from "@tanstack/react-virtual";
import React, { useRef } from "react";
import { CopyIcon, EyeIcon, EyeOffIcon } from "./pwIcons";
import type { InvitationsState } from "./useInvitationsState";

interface InvitationsListProps {
  state: InvitationsState;
}

const InvitationsList: React.FC<InvitationsListProps> = ({ state }) => {
  const {
    invitations,
    loadingInvitations,
    filteredInvitations,
    searchTerm,
    filterStatus,
    assessedClients,
    setSearchTerm,
    setFilterStatus,
    setInviteMessage,
    handleRegenerateLink,
    handleCancelInvitation,
    getStatusBadge,
    RISK_BADGE_CONFIG,
    formatDateShort,
    findAssessmentInList,
  } = state;

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Temp passwords stay masked; only one row can be revealed at a time.
  const [revealedPwId, setRevealedPwId] = React.useState<string | null>(null);

  const pwIconBtnStyle: React.CSSProperties = {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 5,
    cursor: "pointer",
    padding: "3px 8px",
    fontSize: 11,
    fontWeight: 600,
    color: "#475569",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    lineHeight: 1.2,
  };

  const rowVirtualizer = useVirtualizer({
    count: filteredInvitations.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 56,
    overscan: 8,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom =
    virtualItems.length > 0
      ? totalSize - virtualItems[virtualItems.length - 1].end
      : 0;

  return (
    <>
      {/* Invitation History Table */}
      <div className="invitations-table-card">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-1.5 h-6 bg-gradient-to-b from-violet-500 to-purple-600 rounded-full flex-shrink-0" />
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1a1a2e" }}>
            Invitation History
          </h2>
          <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 8 }}>
            {filteredInvitations.length} record{filteredInvitations.length !== 1 ? "s" : ""}
          </span>
        </div>

        <div className="filters-section">
          <div className="filter-group">
            <input
              type="text"
              placeholder="Search by email or organisation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {searchTerm && (
            <button
              className="btn-clear-filters"
              data-testid="clear-filters-btn"
              onClick={() => setSearchTerm("")}
            >
              Clear
            </button>
          )}
        </div>

        <div
          ref={scrollContainerRef}
          className="invitations-table-wrapper"
          style={{ maxHeight: 672, overflow: "auto" }}
        >
          <table className="invitations-table" data-testid="invitations-table">
            <thead>
              <tr>
                <th>C2 Entity Name</th>
                <th>Username</th>
                <th>Temp Password</th>
                <th>Sent By</th>
                <th>Created Date</th>
                <th>Reference</th>
                <th>Sent Date</th>
                <th>Expiry Date</th>
              </tr>
            </thead>
            <tbody>
              {loadingInvitations && invitations.length === 0 ? (
                <tr>
                  <td colSpan={8} className="no-data loading-row">
                    <div className="invitations-loading">
                      <span className="inv-spinner" />
                      <span>Loading invitations…</span>
                    </div>
                  </td>
                </tr>
              ) : filteredInvitations.length === 0 ? (
                <tr>
                  <td colSpan={8} className="no-data">
                    {invitations.length === 0
                      ? "No invitations yet. Create your first invitation to get started."
                      : "No invitations match your filters."}
                  </td>
                </tr>
              ) : (
                <>
                  {paddingTop > 0 && (
                    <tr><td colSpan={8} style={{ height: paddingTop, padding: 0, border: "none" }} /></tr>
                  )}
                  {virtualItems.map((virtualRow) => {
                    const invitation = filteredInvitations[virtualRow.index];
                    if (!invitation) return null;
                    return (
                      <tr
                        key={invitation.id || virtualRow.index}
                        className={`status-${invitation.status || "unknown"}`}
                        data-testid={`invitation-row-${invitation.id}`}
                        data-index={virtualRow.index}
                        ref={rowVirtualizer.measureElement}
                      >
                        <td className="organisation-cell">
                          {invitation.organisationName}
                          {(() => {
                            const a = findAssessmentInList(
                              invitation.organisationName,
                              assessedClients,
                            );
                            if (a) {
                              return (
                                <span
                                  className="row-risk-badge"
                                  style={{
                                    background: RISK_BADGE_CONFIG[a.riskRating].bg,
                                    color: RISK_BADGE_CONFIG[a.riskRating].color,
                                  }}
                                  title={`Risk assessed on ${formatDateShort(a.savedAt)}`}
                                >
                                  {a.riskRating === "HIGH" ? "🔴" : a.riskRating === "MEDIUM" ? "🟡" : "🟢"}{" "}
                                  {a.riskRating}
                                </span>
                              );
                            }
                            return (
                              <span className="row-risk-badge not-assessed" title="No risk assessment on file">
                                ⚪ Not Assessed
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 600, color: "#1e40af" }}>
                          {invitation.c2Username ? (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                              {invitation.c2Username}
                              <button
                                type="button"
                                title="Copy username"
                                onClick={() => {
                                  navigator.clipboard.writeText(invitation.c2Username!);
                                  setInviteMessage("✅ Username copied!");
                                  setTimeout(() => setInviteMessage(""), 2000);
                                }}
                                style={{ background: "none", border: "none", cursor: "pointer", padding: "1px 3px", fontSize: 12, opacity: 0.6 }}
                              >
                                📋
                              </button>
                            </span>
                          ) : "—"}
                        </td>
                        <td style={{ fontFamily: "monospace", fontSize: 12, color: "#374151" }}>
                          {invitation.tempPassword ? (
                            (() => {
                              const rowKey = String(invitation.id ?? virtualRow.index);
                              const revealed = revealedPwId === rowKey;
                              return (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                  <span
                                    style={{
                                      letterSpacing: revealed ? "normal" : "2px",
                                      userSelect: revealed ? "text" : "none",
                                    }}
                                  >
                                    {revealed ? invitation.tempPassword : "••••••••"}
                                  </span>
                                  {!revealed ? (
                                    <button
                                      type="button"
                                      title="Show password"
                                      style={pwIconBtnStyle}
                                      onClick={() => setRevealedPwId(rowKey)}
                                    >
                                      <EyeIcon /> Show
                                    </button>
                                  ) : (
                                    <>
                                      <button
                                        type="button"
                                        title="Copy password"
                                        style={pwIconBtnStyle}
                                        onClick={() => {
                                          navigator.clipboard.writeText(invitation.tempPassword!);
                                          setInviteMessage("✅ Password copied!");
                                          setTimeout(() => setInviteMessage(""), 2000);
                                        }}
                                      >
                                        <CopyIcon /> Copy
                                      </button>
                                      <button
                                        type="button"
                                        title="Hide password"
                                        style={pwIconBtnStyle}
                                        onClick={() => setRevealedPwId(null)}
                                      >
                                        <EyeOffIcon /> Hide
                                      </button>
                                    </>
                                  )}
                                </span>
                              );
                            })()
                          ) : "—"}
                        </td>
                        <td>
                          <span
                            title={
                              !invitation.createdByName && invitation.createdBy
                                ? `Creator identifier: ${invitation.createdBy}`
                                : undefined
                            }
                          >
                            {invitation.createdByName || "Unknown"}
                          </span>
                        </td>
                        <td>{invitation.createdDate || "-"}</td>
                        <td className="reference-cell">{invitation.email || "-"}</td>
                        <td>{formatDateShort(invitation.sentDate)}</td>
                        <td className={invitation.status === "expired" ? "expired-date" : ""}>
                          {formatDateShort(invitation.expiryDate)}
                        </td>
                      </tr>
                    );
                  })}
                  {paddingBottom > 0 && (
                    <tr><td colSpan={8} style={{ height: paddingBottom, padding: 0, border: "none" }} /></tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default InvitationsList;
